import type { ReactNode } from 'react';
import { useState } from 'react';
import type { UseSpringProps } from '@react-spring/web';
import { animated, useSpring } from '@react-spring/web';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { useDimensions } from './use-dimensions';

import * as styles from './grand-entrance.css';

interface GrandEntranceProps {
  show: boolean;
  children: ReactNode;
  className?: string;
  config?: UseSpringProps['config'];
  // Animate the height the container when it is shown/hidden
  animateHeight?: boolean;
}

export const TEST_IDS = {
  CONTAINER: 'grand-entrance--container',
};

export const cubicOutEasing = (t: number): number => --t * t * t + 1;

const DEFAULT_CONFIG = {
  duration: 250,
  easing: cubicOutEasing,
};

export const GrandEntrance = ({
  children,
  show,
  config = DEFAULT_CONFIG,
  animateHeight = true,
  className,
}: GrandEntranceProps) => {
  const [ref, { height }] = useDimensions<HTMLDivElement>();
  const [isAnimating, setIsAnimating] = useState(false);

  const targetHeight = show ? height : 0;

  const animatedStyles = useSpring({
    config,
    height: animateHeight ? targetHeight : undefined,
    opacity: show ? 1 : 0,
    onStart: () => setIsAnimating(true),
    onRest: () => setIsAnimating(false),
  });

  return (
    <animated.div
      className={classFilter([
        className,
        isAnimating ? styles.animating : !show && styles.hide,
      ])}
      style={animatedStyles}
      data-testid={TEST_IDS.CONTAINER}
    >
      <div ref={animateHeight ? ref : undefined}>{children}</div>
    </animated.div>
  );
};
